// src/pages/admin/AdminTownAdminApplications.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, callSetUserRole } from '../../lib/firebase';
import { collection, getDocs, query, where, orderBy, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { PageBack } from '../../components/shared/PageBack';

const AdminTownAdminApplications = () => {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(
        collection(db, 'town_admin_applications'),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      ));
      setApps(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
      toast.error('Failed to load applications');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const approve = async (app) => {
    setBusyId(app.id);
    try {
      await callSetUserRole({ targetUid: app.userId, role: 'town_admin', townId: app.townId });
      await updateDoc(doc(db, 'town_admin_applications', app.id), {
        status: 'approved',
        reviewedAt: serverTimestamp(),
      });
      toast.success(`Town Admin granted for ${app.townName}`);
      setApps(prev => prev.filter(a => a.id !== app.id));
    } catch (err) {
      console.error(err);
      toast.error('Failed to grant Town Admin');
    }
    setBusyId(null);
  };

  const decline = async (app) => {
    if (!window.confirm(`Decline ${app.displayName || app.email}'s application for ${app.townName}?`)) return;
    setBusyId(app.id);
    try {
      await updateDoc(doc(db, 'town_admin_applications', app.id), {
        status: 'declined',
        reviewedAt: serverTimestamp(),
      });
      toast.success('Application declined');
      setApps(prev => prev.filter(a => a.id !== app.id));
    } catch (err) {
      console.error(err);
      toast.error('Failed to update application');
    }
    setBusyId(null);
  };

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', padding: '52px 20px 40px' }}>
      <button onClick={() => navigate('/admin')} style={s.back}>
        <div style={s.backCircle}>←</div>
        <span style={s.backLbl}>Admin</span>
      </button>
      <div style={s.h1}>Town Admin Applications ({apps.length})</div>

      <div style={{ background: 'var(--sage-pale)', color: 'var(--sage-dark)', borderRadius: 12, padding: '10px 14px', fontSize: 12.5, fontWeight: 600, marginBottom: 16, lineHeight: 1.5 }}>
        Approving here grants Town Admin the same way as Admin → Users. Multiple Town Admins per town is fine.
      </div>

      {loading
        ? <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>
        : apps.length === 0
        ? <div style={{ textAlign: 'center', color: 'var(--muted)', padding: 40 }}>No pending applications</div>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {apps.map(app => (
              <div key={app.id} style={s.card}>
                <div style={s.cardTitle}>{app.displayName || app.email || 'Unnamed applicant'}</div>
                <div style={s.cardSub}>{app.email} · applying for <strong>{app.townName}</strong></div>
                <div style={s.reason}>{app.reason}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button
                    onClick={() => approve(app)}
                    disabled={busyId === app.id}
                    style={{ ...s.actionBtn, background: 'var(--sage-deep)', color: 'white', opacity: busyId === app.id ? 0.6 : 1 }}
                  >
                    {busyId === app.id ? '...' : 'Approve — grant Town Admin'}
                  </button>
                  <button
                    onClick={() => decline(app)}
                    disabled={busyId === app.id}
                    style={{ ...s.actionBtn, background: 'var(--error-pale)', color: 'var(--error)', flex: 0.6, opacity: busyId === app.id ? 0.6 : 1 }}
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
      }
      <PageBack position="bottom" fallbackPath="/admin" />
    </div>
  );
};

const s = {
  back: { display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 20 },
  backCircle: { width: 34, height: 34, background: 'var(--white)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: 'var(--sage-dark)', boxShadow: 'var(--shadow-sm)' },
  backLbl: { fontSize: 13, color: 'var(--muted)', fontWeight: 600 },
  h1: { fontSize: 26, fontWeight: 800, color: 'var(--ink)', marginBottom: 16 },
  card: { background: 'var(--white)', borderRadius: 14, padding: '14px 16px', boxShadow: 'var(--shadow-sm)' },
  cardTitle: { fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 2 },
  cardSub: { fontSize: 12, color: 'var(--muted)', fontWeight: 500, marginBottom: 8 },
  reason: { fontSize: 13, color: 'var(--ink)', lineHeight: 1.5, background: 'var(--cream)', borderRadius: 10, padding: '10px 12px' },
  actionBtn: { flex: 1, border: 'none', borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 700, fontFamily: 'Nunito', cursor: 'pointer' },
};

export default AdminTownAdminApplications;
